/**
 * The one shared SVG-element builder for `buildSparkline()`'s geometry
 * (`sparkline.ts`) — `src/panels/VitalsHero.ts` and `src/app/FleetScreen.ts`
 * both render a NAV sparkline and had drifted into two near-identical
 * copies of this DOM-construction code (code-review finding, revamp
 * self-review round 1); this is the single implementation both now call.
 */

import { buildSparkline } from "./sparkline";
import { attachTooltip } from "../ui/tooltip";

const NS = "http://www.w3.org/2000/svg";

export interface SparklineElementOptions {
  readonly width: number;
  readonly height: number;
  readonly padding?: number;
  /** Draws a small dot at the most recent point — the vitals hero wants
   * this "you are here" marker; the fleet cards (many small sparklines on
   * one screen) intentionally omit it to stay uncluttered. */
  readonly endDot?: boolean;
  readonly tooltip?: string;
}

/** Returns `null` (never an empty/placeholder `<svg>`) when there isn't
 * enough series data to draw a real trend — the caller's own "no sparkline
 * yet" honesty rule, same as `buildSparkline` itself. */
export function renderSparklineSvg(
  series: readonly number[],
  options: SparklineElementOptions,
): SVGSVGElement | null {
  const { width, height, padding = 2, endDot = false, tooltip } = options;
  const geo = buildSparkline(series, width, height, padding);
  if (!geo) return null;

  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);

  const stroke =
    geo.trend === "up"
      ? "var(--obs-good)"
      : geo.trend === "down"
        ? "var(--obs-bad)"
        : "var(--obs-text-faint)";

  const path = document.createElementNS(NS, "path");
  path.setAttribute("d", geo.path);
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", stroke);
  path.setAttribute("stroke-width", "1.5");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.appendChild(path);

  if (endDot) {
    const dot = document.createElementNS(NS, "circle");
    dot.setAttribute("cx", String(geo.lastX));
    dot.setAttribute("cy", String(geo.lastY));
    dot.setAttribute("r", "2");
    dot.setAttribute("fill", stroke);
    svg.appendChild(dot);
  }

  if (tooltip) attachTooltip(svg as unknown as HTMLElement, tooltip);
  return svg;
}
